// Uploading a file through the files REST, in two steps: reserve
// (`POST /api/files`), PUT the body to the signed URL it answers, then
// complete (`POST /api/files/:id/complete`). The body never goes through the
// API: cloud signs a storage URL, the offline edition its own
// `/api/local-files/<token>` (under its scheme on the desktop).
//
// The PUT uses XMLHttpRequest, not fetch: fetch cannot report upload progress.
import { sha256 } from "@noble/hashes/sha2.js";
import { bytesToHex } from "@noble/hashes/utils.js";
import {
  FILE_CLIENT_READ_LIMIT,
  type FileDTO,
  type FileUploadDTO,
  isVideoFileMime,
  mimeFromFileName,
} from "@trameria/core/files-shared";
import { m } from "@trameria/i18n";
import type { Api } from "../../data/api";
import { apiErrorMessage } from "../../data/errors";

export interface UploadHandle {
  /** The file as stored, with the SHA-256 read from it here. */
  promise: Promise<FileDTO & { hash: string }>;
  abort(): void;
}

/** The URL the browser loads a file from (`?dl=1`: as an attachment). */
export function fileUrl(
  apiBase: string,
  fileId: string,
  opts: { download?: boolean } = {},
): string {
  return `${apiBase}/api/files/${fileId}${opts.download ? "?dl=1" : ""}`;
}

/**
 * `fileUrl` as a whole URL, for what draws it outside this page (the phone
 * app's own menu): the same address, with the page's origin when the API is
 * on it.
 */
export function wholeFileUrl(apiBase: string, fileId: string): string {
  return new URL(fileUrl(apiBase, fileId), window.location.href).href;
}

/** "12 KB": the size of a file, as chips show it. */
export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** The reason given when an upload was aborted (fixed, so callers tell it from a failure). */
export const uploadAborted = (): string => m.blocks_upload_aborted();

/** The one line shown for a failed upload, the same wherever files go up. */
export function uploadErrorMessage(error: unknown): string {
  const fallback =
    error instanceof Error && error.message
      ? error.message
      : m.blocks_upload_failed();
  return apiErrorMessage(error, fallback);
}

/**
 * The type of a file to upload: `file.type`, or from the name when empty (some
 * devices give .mov / .webm no type; without it neither the video limit nor
 * the video syntax would apply). The reserve's `mime` and the body's form are
 * both decided from this.
 */
export function uploadMimeOf(file: File): string {
  return file.type || mimeFromFileName(file.name);
}

/**
 * An image's size, sent with the reserve so the files table has it (readers
 * do not ask for it; it is there if a screen ever wants to reserve space).
 * Nothing when it cannot be measured.
 */
export async function imageDimensions(
  file: File,
): Promise<{ width: number; height: number } | undefined> {
  if (!file.type.startsWith("image/")) return undefined;
  if (typeof createImageBitmap !== "function") return undefined;
  try {
    const bitmap = await createImageBitmap(file);
    const size = { width: bitmap.width, height: bitmap.height };
    bitmap.close();
    return size;
  } catch {
    return undefined;
  }
}

/** How long a video's size is waited for before its upload starts without it. */
const VIDEO_MEASURE_MS = 2000;

/**
 * A video's size from its metadata (only the head of the file is read), so a
 * message can take its thumbnail's box before the video loads. Nothing when it
 * cannot be read in time.
 */
export function videoDimensions(
  file: File,
  mime: string,
): Promise<{ width: number; height: number } | undefined> {
  if (!isVideoFileMime(mime) || typeof document === "undefined")
    return Promise.resolve(undefined);
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    const done = (size?: { width: number; height: number }) => {
      clearTimeout(timer);
      video.onloadedmetadata = null;
      video.onerror = null;
      video.removeAttribute("src");
      video.load();
      URL.revokeObjectURL(url);
      resolve(size);
    };
    const timer = setTimeout(() => done(), VIDEO_MEASURE_MS);
    video.preload = "metadata";
    video.muted = true;
    video.onloadedmetadata = () =>
      done(
        video.videoWidth > 0 && video.videoHeight > 0
          ? { width: video.videoWidth, height: video.videoHeight }
          : undefined,
      );
    video.onerror = () => done();
    video.src = url;
  });
}

/**
 * SHA-256 of the content (64 lowercase hex). With it, the server reuses the
 * same content already uploaded (`upload: null`), and the upload URL fixes it:
 * the storage refuses a body with another hash.
 *
 * Read in chunks: videos go up to 200MB, and `crypto.subtle` cannot hash
 * incrementally - reading the whole file into memory froze phones.
 * `isAborted` is checked per chunk so an abort does not wait for 200MB.
 */
export async function sha256Hex(
  blob: Blob,
  isAborted?: () => boolean,
): Promise<string> {
  const hash = sha256.create();
  if (typeof blob.stream !== "function") {
    hash.update(new Uint8Array(await blob.arrayBuffer()));
    return bytesToHex(hash.digest());
  }
  const reader = blob.stream().getReader();
  let finished = false;
  try {
    for (;;) {
      if (isAborted?.()) throw new Error(uploadAborted());
      const { done, value } = await reader.read();
      if (done) break;
      hash.update(value);
    }
    finished = true;
  } finally {
    // Aborted or failed: release the half-read stream.
    if (!finished) void reader.cancel().catch(() => {});
  }
  return bytesToHex(hash.digest());
}

/** PUTs the body with progress. Rejects with a readable reason. */
function putBody(
  upload: FileUploadDTO,
  file: File,
  onProgress: ((ratio: number) => void) | undefined,
  track: (xhr: XMLHttpRequest) => void,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const x = new XMLHttpRequest();
    track(x);
    x.open(upload.method, upload.url);
    // The headers are signed: send exactly what the server gave (more is a 403).
    for (const [k, v] of Object.entries(upload.headers)) {
      x.setRequestHeader(k, v);
    }
    x.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(e.loaded / e.total);
    };
    x.onload = () => {
      if (x.status >= 200 && x.status < 300) resolve();
      else
        reject(new Error(m.blocks_upload_failed_status({ status: x.status })));
    };
    x.onerror = () => reject(new Error(m.blocks_upload_failed_network()));
    x.onabort = () => reject(new Error(uploadAborted()));
    x.send(file);
  });
}

export function uploadFile(
  api: Api,
  file: File,
  opts: {
    onProgress?: (ratio: number) => void;
    dimensions?: { width: number; height: number };
  } = {},
): UploadHandle {
  let xhr: XMLHttpRequest | null = null;
  let aborted = false;

  const promise = (async () => {
    // Refuse what is far too large before reading it to hash (the hash comes
    // before the reserve now, so the body is read first). Not the server's
    // limit, only a guard against loading gigabytes into the tab.
    if (file.size > FILE_CLIENT_READ_LIMIT)
      throw new Error(m.blocks_upload_too_large());
    const hash = await sha256Hex(file, () => aborted);
    // Aborted while reading: no reserve (it would leave a pending row to purge).
    if (aborted) throw new Error(uploadAborted());
    const { file: created, upload } = await api<{
      file: FileDTO;
      // null: the same content is already there (not uploaded again).
      upload: FileUploadDTO | null;
    }>("/api/files", {
      method: "POST",
      body: {
        name: file.name,
        mime: uploadMimeOf(file),
        size: file.size,
        hash,
        ...(opts.dimensions ?? {}),
      },
    });
    if (aborted) throw new Error(uploadAborted());
    if (upload) {
      await putBody(upload, file, opts.onProgress, (x) => {
        xhr = x;
      });
    } else {
      // Reused: no body goes up, so no progress event would ever fire and the
      // meter would look stuck. Fill it once.
      opts.onProgress?.(1);
    }
    // Complete is called either way (it returns early for a ready row), so the
    // client has one path.
    const { file: ready } = await api<{ file: FileDTO }>(
      `/api/files/${created.id}/complete`,
      { method: "POST" },
    );
    // The hash read here goes with it (what a signed post names the file by).
    return { ...ready, hash };
  })();

  return {
    promise,
    abort: () => {
      aborted = true;
      (xhr as XMLHttpRequest | null)?.abort();
    },
  };
}
